import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Car, ScanLine, Wand2, Send, Sparkles, ArrowRight, CheckCircle2, Clock,
  Camera, ShieldCheck, Globe, MessageCircle, Instagram, Facebook, FileDown,
  Building2, Users, Truck, Menu, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ComparisonSlider from "@/components/ComparisonSlider";

const BEFORE_IMG = "/landing/fotobooth-before.jpg";
const AFTER_IMG = "/landing/fotobooth-after.jpg";

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`px-5 lg:px-8 ${className}`}>
    <div className="mx-auto max-w-6xl">{children}</div>
  </section>
);

const Kicker = ({ children }) => (
  <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-sky-400 mb-3">{children}</div>
);

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Navigation */}
      <header className={`fixed inset-x-0 top-0 z-50 transition-colors ${scrolled ? "bg-background/85 backdrop-blur border-b border-border" : "bg-transparent"}`}>
        <div className="mx-auto max-w-6xl px-5 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 border border-primary/30">
              <Car className="h-4 w-4 text-primary" />
            </div>
            <span className="font-display text-lg font-bold">CarOS</span>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
            <a href="#loesung" className="hover:text-foreground transition-colors">So funktioniert's</a>
            <a href="#fotobooth" className="hover:text-foreground transition-colors">Fotobooth</a>
            <a href="#inserat" className="hover:text-foreground transition-colors">KI-Inserat</a>
            <a href="#content" className="hover:text-foreground transition-colors">Content Studio</a>
            <a href="#preis" className="hover:text-foreground transition-colors">Preis</a>
          </nav>
          <div className="hidden md:flex items-center gap-2">
            <Button variant="outline" size="sm" asChild data-testid="landing-nav-request-button">
              <Link to="/registrieren">Pilotpartner werden</Link>
            </Button>
            <Button size="sm" className="font-semibold" asChild data-testid="landing-nav-demo-button">
              <Link to="/login">Demo starten</Link>
            </Button>
          </div>
          <button className="md:hidden p-2" onClick={() => setMenuOpen(!menuOpen)} data-testid="landing-mobile-menu-button" aria-label="Menü">
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-border bg-background/95 backdrop-blur px-5 py-4 space-y-3">
            {[["#loesung", "So funktioniert's"], ["#fotobooth", "Fotobooth"], ["#inserat", "KI-Inserat"], ["#content", "Content Studio"], ["#preis", "Preis"]].map(([href, label]) => (
              <a key={href} href={href} onClick={() => setMenuOpen(false)} className="block text-sm text-muted-foreground">{label}</a>
            ))}
            <Button className="w-full font-semibold" asChild><Link to="/login">Demo starten</Link></Button>
            <Button variant="outline" className="w-full" asChild><Link to="/registrieren">Pilotpartner werden – 199 €/Monat</Link></Button>
          </div>
        )}
      </header>

      {/* Hero */}
      <Section className="pt-28 lg:pt-36 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(900px 400px at 20% -10%, rgba(56,189,248,0.14), transparent 70%)" }} />
        <div className="relative grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Kicker>Das KI-Publishing-System für Autohändler</Kicker>
            <h1 className="font-display text-4xl lg:text-5xl xl:text-[3.4rem] font-bold leading-[1.05] tracking-[-0.03em]" data-testid="landing-hero-headline">
              Vom Fahrzeugankauf zum Inserat <span className="text-primary">in wenigen Minuten.</span>
            </h1>
            <p className="text-base lg:text-lg text-muted-foreground mt-5 max-w-xl">
              FIN scannen, Fotos aufnehmen, professionelle Studio-Bilder erstellen und das Fahrzeug
              auf allen wichtigen Kanälen veröffentlichen – direkt vom Parkplatz, noch vor der Aufbereitung.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Button size="lg" className="h-12 px-7 font-semibold gap-2" asChild data-testid="landing-hero-demo-button">
                <Link to="/login">Demo starten <ArrowRight className="h-4 w-4" /></Link>
              </Button>
              <Button size="lg" variant="outline" className="h-12 px-7" asChild data-testid="landing-hero-request-button">
                <a href="#preis">Preis ansehen</a>
              </Button>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 text-sm text-muted-foreground">
              {["Fotografieren.", "Daten laden.", "Veröffentlichen."].map((w) => (
                <span key={w} className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> {w}</span>
              ))}
            </div>
          </div>
          <div className="relative">
            <ComparisonSlider beforeSrc={BEFORE_IMG} afterSrc={AFTER_IMG} beforeLabel="Parkplatz" afterLabel="CarOS Fotobooth" testId="landing-hero-slider" className="shadow-2xl shadow-sky-500/10" />
            <p className="text-[11px] text-muted-foreground mt-2 text-center">Echte Fotobooth-Ausgabe – Fahrzeug unverändert, nur Umgebung & Bildqualität optimiert.</p>
          </div>
        </div>
      </Section>

      {/* Problem */}
      <Section className="py-16 border-t border-border/60">
        <Kicker>Das Problem</Kicker>
        <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-[-0.02em] max-w-2xl">
          Zwischen Ankauf und Inserat vergehen oft Tage bis Wochen.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {[
            { icon: Car, t: "Fahrzeug angekauft", d: "Das Fahrzeug steht beim Verkäufer, bei der Auktion oder auf einem fremden Hof." },
            { icon: Truck, t: "Transport & Aufbereitung", d: "Logistik, Werkstatt und Aufbereitung brauchen Zeit – oft mehrere Wochen." },
            { icon: Clock, t: "Inserat kommt zu spät", d: "Erst danach entstehen Fotos und Texte. Das Inserat geht viel zu spät online." },
            { icon: Users, t: "Keine Interessenten", d: "In dieser Zeit sieht kein Kunde das Fahrzeug – Standtage und Kapitalbindung steigen." },
          ].map((x, i) => (
            <div key={i} className="rounded-2xl border border-border bg-card p-5" data-testid={`landing-problem-${i + 1}`}>
              <x.icon className="h-5 w-5 text-muted-foreground" />
              <div className="font-semibold mt-3">{x.t}</div>
              <p className="text-sm text-muted-foreground mt-1.5">{x.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Loesung */}
      <Section id="loesung" className="py-16">
        <Kicker>Die Lösung</Kicker>
        <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-[-0.02em]">Drei Schritte. Ein Gerät. Sofort online.</h2>
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          {[
            { icon: ScanLine, t: "1. FIN eingeben", d: "Fahrzeugdaten, Motorisierung und Ausstattung werden automatisch geladen." },
            { icon: Wand2, t: "2. Fotos mit KI-Fotobooth optimieren", d: "Smartphone-Fotos werden zu professionellen Studio-Bildern – das Fahrzeug bleibt unverändert." },
            { icon: Send, t: "3. Inserat automatisch veröffentlichen", d: "Die KI schreibt das Inserat, Sie prüfen und veröffentlichen auf allen Kanälen." },
          ].map((x, i) => (
            <div key={i} className="rounded-2xl border border-primary/20 bg-card p-6 relative overflow-hidden" data-testid={`landing-solution-${i + 1}`}>
              <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(300px 120px at 20% 0%, rgba(56,189,248,0.08), transparent 70%)" }} />
              <div className="relative">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 border border-primary/30">
                  <x.icon className="h-5 w-5 text-primary" />
                </div>
                <div className="font-display font-bold mt-4">{x.t}</div>
                <p className="text-sm text-muted-foreground mt-1.5">{x.d}</p>
              </div>
            </div>
          ))}
        </div>

        {/* USP Banner */}
        <div className="mt-8 rounded-2xl border border-primary/30 bg-primary/5 p-6 lg:p-8 flex flex-col lg:flex-row lg:items-center gap-5" data-testid="landing-usp-banner">
          <div className="flex-1">
            <div className="font-display text-xl lg:text-2xl font-bold">Der entscheidende Vorteil: Pre-Listing.</div>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Ihr Fahrzeug kann bereits online sein, bevor es bei Ihnen angekommen und aufbereitet ist.
              Interessenten entstehen, während Transport und Aufbereitung parallel laufen.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {["Neu im Zulauf", "Frisch angekauft", "Weitere Fotos folgen", "Verfügbar ab Datum"].map((c) => (
              <span key={c} className="rounded-full border border-sky-500/30 bg-sky-500/10 text-sky-300 px-3 py-1 text-xs">{c}</span>
            ))}
          </div>
        </div>
      </Section>

      {/* Fotobooth */}
      <Section id="fotobooth" className="py-16 border-t border-border/60">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="order-2 lg:order-1">
            <ComparisonSlider beforeSrc={BEFORE_IMG} afterSrc={AFTER_IMG} beforeLabel="Vorher" afterLabel="Nachher" testId="landing-fotobooth-slider" className="shadow-2xl shadow-sky-500/10" />
          </div>
          <div className="order-1 lg:order-2">
            <Kicker>KI-Fotobooth</Kicker>
            <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-[-0.02em]">Parkplatzfoto rein. Studio-Bild raus.</h2>
            <p className="text-sm lg:text-base text-muted-foreground mt-3">
              Fotografieren Sie das Fahrzeug in beliebiger Umgebung. Die Fotobooth erstellt daraus
              einheitliche, professionelle Händlerbilder – mit Studiohintergrund, realistischem Schatten
              und Ihrem Logo.
            </p>
            <div className="grid sm:grid-cols-2 gap-2.5 mt-6 text-sm">
              {[
                "Hintergrund durch Studio ersetzen", "Realistischer Fahrzeugschatten",
                "Kennzeichen unkenntlich machen", "Belichtung & Weissabgleich",
                "Händlerlogo & Wasserzeichen", "Einheitlicher Bildausschnitt",
              ].map((f) => (
                <div key={f} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary shrink-0" /> {f}</div>
              ))}
            </div>
            <div className="mt-5 rounded-xl border border-border bg-card p-3.5 flex items-start gap-2.5 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
              Ehrlichkeits-Garantie: Die KI verändert niemals das Fahrzeug selbst. Keine entfernten Kratzer,
              keine geänderten Farben, keine erfundene Ausstattung – nur Umgebung und Bildqualität.
            </div>
          </div>
        </div>
      </Section>

      {/* KI-Inserat */}
      <Section id="inserat" className="py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Kicker>KI-Inserat</Kicker>
            <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-[-0.02em]">Die KI schreibt. Sie prüfen und bestätigen.</h2>
            <p className="text-sm lg:text-base text-muted-foreground mt-3">
              Aus Fahrzeugdaten und Ihren Hinweisen entstehen Börsentitel, Beschreibung und
              Ausstattungs-Highlights – in Ihrer Händler-Tonalität.
            </p>
            <div className="space-y-2.5 mt-6 text-sm">
              {[
                "Fahrzeugdaten automatisch übernommen",
                "Beschreibung automatisch erstellt – sachlich, emotional oder Premium",
                "Ausstattung automatisch strukturiert",
                "Sensible Angaben wie „unfallfrei“ nur nach Ihrer Bestätigung",
              ].map((f) => (
                <div key={f} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" /> {f}</div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5 shadow-xl" data-testid="landing-listing-mock">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Börsentitel</div>
            <div className="font-display font-bold mt-1">BMW 320d Touring M Sport · AHK · Head-Up · Laser</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-4">Beschreibung</div>
            <p className="text-sm text-muted-foreground mt-1">
              Dieser gepflegte 320d Touring kombiniert Effizienz mit sportlicher Eleganz. Das M Sportpaket,
              Head-Up-Display und Laserlicht machen ihn zum idealen Begleiter für Familie und Langstrecke...
            </p>
            <div className="flex flex-wrap gap-1.5 mt-4">
              {["M Sportpaket", "AHK schwenkbar", "Head-Up", "Laserlicht", "ACC"].map((h) => (
                <span key={h} className="rounded-full border border-primary/30 bg-primary/10 text-primary px-2.5 py-1 text-xs">{h}</span>
              ))}
            </div>
            <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
              Bitte bestätigen: Das Fahrzeug darf als „unfallfrei“ beschrieben werden.
            </div>
          </div>
        </div>
      </Section>

      {/* Kanaele */}
      <Section className="py-16 border-t border-border/60">
        <Kicker>Überall veröffentlichen</Kicker>
        <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-[-0.02em] max-w-2xl">
          Ein Fahrzeug einmal aufnehmen. Überall professionell verkaufen.
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8" data-testid="landing-channels-grid">
          {[
            { icon: Globe, n: "mobile.de" }, { icon: Globe, n: "AutoScout24" },
            { icon: Building2, n: "Händlerhomepage" }, { icon: Instagram, n: "Instagram" },
            { icon: Facebook, n: "Facebook" }, { icon: MessageCircle, n: "WhatsApp" },
            { icon: Globe, n: "Google Business" }, { icon: FileDown, n: "CSV / XML-Feed" },
          ].map((c) => (
            <div key={c.n} className="rounded-xl border border-border bg-card px-4 py-4 flex items-center gap-2.5 text-sm">
              <c.icon className="h-4 w-4 text-primary" /> {c.n}
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Plattform-Anbindungen werden gemeinsam mit unseren Pilotpartnern eingerichtet – transparent, ohne vorgetäuschte Live-Verbindungen.
        </p>
      </Section>

      {/* Content Studio */}
      <Section id="content" className="py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Kicker>Content Studio</Kicker>
            <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-[-0.02em]">Aus denselben Bildern: Social-Media-Content.</h2>
            <p className="text-sm lg:text-base text-muted-foreground mt-3">
              Kein separates Fotoshooting, kein erneutes Schreiben. Das Content Studio erstellt aus
              Fotobooth-Bildern und Fahrzeugdaten fertige Entwürfe für Ihre Kanäle.
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              {["Instagram-Post", "Story", "Reel-Skript", "Facebook-Post", "WhatsApp-Status", "Neu eingetroffen", "Preis reduziert", "Verkauft"].map((c) => (
                <span key={c} className="rounded-full border border-border bg-secondary/40 px-3 py-1.5 text-xs">{c}</span>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { t: "Instagram-Post", d: "Gerade eingetroffen: BMW 320d Touring – M Sport, Head-Up & Laser. Jetzt Probefahrt sichern!", icon: Instagram },
              { t: "Reel-Skript", d: "Szene 1: Frontale Nähe. Szene 2: Cockpit-Schwenk. Szene 3: Preis-Einblendung...", icon: Camera },
              { t: "WhatsApp-Status", d: "Neu bei uns: 320d Touring, EZ 2021, 68.500 km – Details im Link.", icon: MessageCircle },
              { t: "Neu im Zulauf", d: "Dieser 320d ist unterwegs zu uns – jetzt schon reservieren.", icon: Truck },
            ].map((c) => (
              <div key={c.t} className="rounded-xl border border-border bg-card p-4" data-testid={`landing-content-${c.t}`}>
                <div className="flex items-center gap-2 text-xs font-semibold"><c.icon className="h-3.5 w-3.5 text-primary" /> {c.t}</div>
                <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Fuer wen */}
      <Section className="py-16 border-t border-border/60">
        <Kicker>Für wen</Kicker>
        <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-[-0.02em]">Gebaut für den Fahrzeughandel.</h2>
        <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-8">
          {["Freie Autohändler", "Markengebundene Händler", "Händlergruppen", "Importeure", "Fahrzeugankäufer"].map((t) => (
            <div key={t} className="rounded-xl border border-border bg-card px-4 py-5 text-sm font-medium text-center">{t}</div>
          ))}
        </div>
      </Section>

      {/* Preis */}
      <Section id="preis" className="py-16 border-t border-border/60">
        <div className="text-center mb-8">
          <Kicker>Preis</Kicker>
          <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-[-0.02em]">Ein Tarif. Alles drin. Jederzeit kündbar.</h2>
        </div>
        <div className="mx-auto max-w-md">
          <div className="relative overflow-hidden rounded-3xl border border-primary/30 bg-card p-7" data-testid="landing-pricing-card">
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(400px 180px at 50% 0%, rgba(56,189,248,0.14), transparent 70%)" }} />
            <div className="relative">
              <span className="inline-flex rounded-full bg-primary/15 border border-primary/30 text-primary text-[10px] font-bold uppercase tracking-widest px-2.5 py-1">Pilotpartner-Tarif</span>
              <div className="flex items-end gap-2 mt-4">
                <span className="font-display text-5xl font-bold tabular-nums" data-testid="landing-price">199&nbsp;€</span>
                <span className="text-sm text-muted-foreground pb-2">/ Monat</span>
              </div>
              <p className="text-sm text-emerald-300 font-medium mt-1">Jederzeit kündbar – keine Mindestlaufzeit</p>
              <div className="space-y-2 mt-5 text-sm">
                {["Unbegrenzte Fahrzeuge & Inserate", "KI-Fotobooth & FIN-Scan inklusive", "KI-Inserate & Content Studio", "Multi-Channel-Veröffentlichung", "Persönliches Onboarding"].map((f) => (
                  <div key={f} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary shrink-0" /> {f}</div>
                ))}
              </div>
              <Button size="lg" className="w-full h-12 mt-6 font-semibold gap-2" asChild data-testid="landing-pricing-cta">
                <Link to="/registrieren">Pilotpartner werden <ArrowRight className="h-4 w-4" /></Link>
              </Button>
              <p className="text-[10px] text-muted-foreground text-center mt-3">Keine Zahlungsdaten nötig – Abrechnung startet nach dem gemeinsamen Onboarding.</p>
            </div>
          </div>
        </div>
      </Section>

      {/* Final CTA */}
      <Section id="kontakt" className="py-20">
        <div className="relative overflow-hidden rounded-3xl border border-primary/30 bg-card p-8 lg:p-14 text-center">
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(700px 300px at 50% 0%, rgba(56,189,248,0.16), transparent 70%)" }} />
          <div className="relative">
            <h2 className="font-display text-2xl lg:text-4xl font-bold tracking-[-0.02em] max-w-3xl mx-auto" data-testid="landing-final-cta-headline">
              Bringen Sie Ihr nächstes Fahrzeug online, bevor es bei Ihnen auf dem Hof steht.
            </h2>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <Button size="lg" className="h-12 px-8 font-semibold gap-2" asChild data-testid="landing-final-demo-button">
                <Link to="/login">Demo starten <ArrowRight className="h-4 w-4" /></Link>
              </Button>
              <Button size="lg" variant="outline" className="h-12 px-8" asChild data-testid="landing-final-pilot-button">
                <Link to="/registrieren">Pilotpartner werden – 199 €/Monat</Link>
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-4">Jederzeit kündbar · Keine Mindestlaufzeit · Persönliches Onboarding</p>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="border-t border-border px-5 lg:px-8 py-8">
        <div className="mx-auto max-w-6xl flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <Car className="h-4 w-4 text-primary" />
            <span className="font-display font-bold text-foreground">CarOS</span>
            <span>· Vehicle-to-Market in Minuten</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login" className="hover:text-foreground transition-colors" data-testid="landing-footer-login">Anmelden</Link>
            <a
              href="https://cloudservice.hr"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground transition-colors"
              data-testid="landing-footer-developer-link"
            >
              Entwickelt von cloudservice.hr
            </a>
            <a
              href="https://www.instagram.com/cloudservice.hr"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground transition-colors"
              aria-label="Instagram: cloudservice.hr"
              data-testid="landing-footer-instagram-link"
            >
              <Instagram className="h-4 w-4" />
            </a>
            <span>© 2026 CarOS</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
